/* eslint-disable unicorn/prefer-dom-node-append */
import { DOCUMENT, isPlatformServer } from '@angular/common';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable, Injector, PLATFORM_ID } from '@angular/core';
import {
  makeStateKey,
  Meta,
  Title,
  TransferState,
} from '@angular/platform-browser';

import { REQUEST } from '@nguniversal/express-engine/tokens';
// import cssVars from 'css-vars-ponyfill';
import { Request } from 'express';
import {
  EMPTY,
  fromEvent,
  merge,
  Observable,
  of,
  Subscription,
  timer,
} from 'rxjs';
import {
  catchError,
  exhaustMap,
  filter,
  first,
  map,
  switchMap,
} from 'rxjs/operators';

import { environment } from '@env/environment';

import { StorageKeyEnum } from '@app/core/enums/storage-key.enum';
import { ISettingsApiModel } from '@app/core/models/api';
import { APP_CONFIG, IAppConfig } from './app.config';
import { StorageService } from './storage.service';

const SETTINGS_TSTATE_KEY = makeStateKey<ISettingsApiModel>('settings');
const SETTINGS_REVALIDATE_INTERVAL_MS = 300_000;
const SETTINGS_RELOAD_MARKER_KEY = 'btr-settings-reload-version';
const STRUCTURAL_SETTINGS_KEYS: Array<keyof ISettingsApiModel> = [
  'cluster',
  'config_name',
  'custom_domain',
  'is_white_label',
  'is_deals_hidden',
  'is_events_hidden',
  'is_guides_hidden',
  'is_places_hidden',
  'is_passports_hidden',
];

@Injectable({ providedIn: 'root' })
export class AppLoadService {
  private readonly isServer: boolean;
  private settingsRevalidationSubscription?: Subscription;

  constructor(
    @Inject(APP_CONFIG) private appConfig: IAppConfig,
    @Inject(DOCUMENT) private readonly document: Document,
    @Inject(PLATFORM_ID) private readonly platformId: any,
    private readonly injector: Injector,
    private readonly http: HttpClient,
    private readonly storageService: StorageService,
    private readonly titleService: Title,
    private readonly metaService: Meta,
    private readonly transferState: TransferState,
  ) {
    this.isServer = isPlatformServer(this.platformId);
  }

  initializeApp(): Promise<void> {
    this.checkVersion();

    return new Promise((resolve, reject) => {
      if (!this.isServer) {
        const settings = this.transferState.get<ISettingsApiModel>(
          SETTINGS_TSTATE_KEY,
          null,
        );

        if (settings != null) {
          this.transferState.remove<ISettingsApiModel>(SETTINGS_TSTATE_KEY);
          const initialSettings = this.getInitialBrowserSettings(settings);
          this.initSettings(initialSettings);
          this.startSettingsRevalidation(initialSettings.config_name, true);
          resolve();
          return;
        }
      }

      if (
        this.appConfig &&
        this.appConfig.settings &&
        this.appConfig.settings.config_name
      ) {
        if (this.isServer) {
          this.transferState.set<ISettingsApiModel>(
            SETTINGS_TSTATE_KEY,
            this.appConfig.settings,
          );
        }

        this.initSettings(this.appConfig.settings);
        this.startSettingsRevalidation(
          this.appConfig.settings.config_name,
          true,
        );
        resolve();
        return;
      }

      this.getConfigName()
        .pipe(
          map(name => (name != null ? name : environment.DEFAULT_CONFIG_NAME)),
          switchMap(name => {
            return this.getConfig(name);
          }),
          catchError(err => {
            console.log('getConfigName catchError', err);

            return this.getConfig(environment.DEFAULT_CONFIG_NAME);
          }),
          first(),
        )
        .subscribe(
          data => {
            if (this.isServer) {
              this.transferState.set<ISettingsApiModel>(
                SETTINGS_TSTATE_KEY,
                data,
              );
            }
            this.initSettings(data);
            this.startSettingsRevalidation(data.config_name, false);
            resolve();
          },
          err => {
            console.log('ERROR', err);
            reject(new Error(err));
          },
        );
    });
  }

  private initSettings(
    data: ISettingsApiModel,
    updateDocumentTitle = true,
  ): void {
    Object.assign(this.appConfig.settings, data);

    this.appConfig.configsUrl = environment.WLS_CONFIGS_URL.replace(
      '[[[WL_NAME]]]',
      data.config_name,
    );

    this.initHeadMetatag(this.appConfig.configsUrl, data);
    this.initMetatagHeaderColor(data);
    this.storageService.CONFIG = data;
    if (updateDocumentTitle) {
      this.titleService.setTitle(data.page_title);
    }
    if (data.config_name === 'core') {
      data.main_color = '#EB5739';
      (
        this.document.documentElement as any
      ).style = `--mainColor: #EB5739; --mainColorOp16: #EB573921; --secondColor: #1448CC; --linkColor: ${data.second_color}; --mainColor: '#EB5739; --headerTextColor: #FFFFFF;`;
    } else {
      const secondColor = data.second_color
        ? data.second_color
        : data.main_color;
      const linkColor = data.second_color ? data.second_color : data.main_color;
      (this.document.documentElement as any).style = `--mainColor: ${
        data.main_color
      }; --mainColorOp16: ${
        data.main_color
      }21; --secondColor: ${secondColor}; --linkColor: ${linkColor}; --headerTextColor: ${
        data.header_color === data.main_color
          ? data.breadcrumb_color
          : data.header_color
      };`;
    }
  }

  private startSettingsRevalidation(
    configName: string,
    refreshImmediately: boolean,
  ): void {
    if (this.isServer || !configName || this.settingsRevalidationSubscription) {
      return;
    }

    const firstRefreshDelay = refreshImmediately
      ? 0
      : SETTINGS_REVALIDATE_INTERVAL_MS;
    const periodicRefresh$ = timer(
      firstRefreshDelay,
      SETTINGS_REVALIDATE_INTERVAL_MS,
    );
    const tabBecameVisible$ = fromEvent(this.document, 'visibilitychange').pipe(
      filter(() => this.document.visibilityState === 'visible'),
    );

    // TransferState makes the first paint fast; this request checks for fresher API settings.
    this.settingsRevalidationSubscription = merge(
      periodicRefresh$,
      tabBecameVisible$,
    )
      .pipe(
        exhaustMap(() =>
          this.getConfig(configName, true).pipe(
            catchError(error => {
              // A refresh failure must never prevent the consumer app from working.
              console.warn('Settings background revalidation failed', error);
              return EMPTY;
            }),
          ),
        ),
      )
      .subscribe(settings => this.applyRevalidatedSettings(settings));
  }

  private applyRevalidatedSettings(settings: ISettingsApiModel): void {
    const currentSettings = this.storageService.CONFIG;
    if (
      !currentSettings ||
      !this.haveSettingsChanged(currentSettings, settings)
    ) {
      return;
    }

    const requiresReload = STRUCTURAL_SETTINGS_KEYS.some(
      key => currentSettings[key] !== settings[key],
    );

    // Preserve the route-specific title while applying visual settings in place.
    this.initSettings(settings, false);

    if (!requiresReload) {
      return;
    }

    const browserWindow = this.document.defaultView;
    const settingsIdentity = this.getSettingsIdentity(settings);
    if (
      !browserWindow ||
      browserWindow.sessionStorage.getItem(SETTINGS_RELOAD_MARKER_KEY) ===
        settingsIdentity
    ) {
      return;
    }

    // Prevent a reload loop while SSR nodes may still hold an older settings cache.
    browserWindow.sessionStorage.setItem(
      SETTINGS_RELOAD_MARKER_KEY,
      settingsIdentity,
    );
    browserWindow.location.reload();
  }

  private haveSettingsChanged(
    currentSettings: ISettingsApiModel,
    nextSettings: ISettingsApiModel,
  ): boolean {
    const currentVersion =
      currentSettings.settings_version ?? currentSettings.date_modified;
    const nextVersion =
      nextSettings.settings_version ?? nextSettings.date_modified;

    if (currentVersion != null && nextVersion != null) {
      return `${currentVersion}` !== `${nextVersion}`;
    }

    // Older APIs have no revision field, so compare the complete payload.
    return JSON.stringify(currentSettings) !== JSON.stringify(nextSettings);
  }

  private getSettingsIdentity(settings: ISettingsApiModel): string {
    const version = settings.settings_version ?? settings.date_modified;
    if (version != null) {
      return `version:${version}`;
    }

    const serializedSettings = JSON.stringify(settings);
    let hash = 0;
    for (let index = 0; index < serializedSettings.length; index++) {
      hash =
        (hash * 31 + (serializedSettings.codePointAt(index) ?? 0)) %
        2_147_483_647;
    }

    return `hash:${hash}`;
  }

  private getInitialBrowserSettings(
    transferredSettings: ISettingsApiModel,
  ): ISettingsApiModel {
    const storedSettings = this.storageService.CONFIG;
    const reloadMarker = this.document.defaultView?.sessionStorage.getItem(
      SETTINGS_RELOAD_MARKER_KEY,
    );

    // Keep the freshly revalidated config across one reload while SSR cache catches up.
    if (
      storedSettings &&
      reloadMarker &&
      reloadMarker === this.getSettingsIdentity(storedSettings) &&
      reloadMarker !== this.getSettingsIdentity(transferredSettings)
    ) {
      return storedSettings;
    }

    return transferredSettings;
  }

  private checkVersion(): void {
    const currentVer: string = environment.version;
    const previousVer: string = this.storageService.VERSION;

    if (currentVer !== previousVer) {
      this.storageService.removeKey(StorageKeyEnum.VERSION);
      this.storageService.removeKey(StorageKeyEnum.CONFIG);
      this.storageService.removeKey(StorageKeyEnum.CLUSTERS);
      this.storageService.removeKey(StorageKeyEnum.COLLECTIONS);
      this.storageService.removeKey(StorageKeyEnum.PLACES);
      this.storageService.setPlaces(null);
      this.storageService.removeKey(StorageKeyEnum.GUIDES);
      this.storageService.removeKey(StorageKeyEnum.BOOKMARK_IDS);
      this.storageService.removeKey(StorageKeyEnum.SELECTED_CLUSTER);
      this.storageService.removeKey(StorageKeyEnum.GDRP);
      this.storageService.removeKey(StorageKeyEnum.DESTINATION_ON_SLIDER);
      this.storageService.removeKey(StorageKeyEnum.IS_ERROR_SHOW);

      // AUTH KEYS
      // this.storageService.removeKey(StorageKeyEnum.USER);
      // this.storageService.removeKey(StorageKeyEnum.TOKEN);
      // this.storageService.removeKey(StorageKeyEnum.PROFILE);
      // this.storageService.removeKey(StorageKeyEnum.PLANS);

      this.storageService.VERSION = currentVer;
    }
  }

  private getConfigName(): Observable<string> {
    if (this.isServer) {
      const serverRequest = this.injector.get(REQUEST) as Request;
      return of(serverRequest.get('core'));
    }

    const url: string = '/';

    return this.http
      .get(url, {
        observe: 'response',
        responseType: 'text',
        headers: { isconfig: 'true' },
      })
      .pipe(
        map(res => {
          return res.headers.get('Core');
        }),
      );
  }

  private getConfig(
    name: string,
    bypassCache = false,
  ): Observable<ISettingsApiModel> {
    let params = new HttpParams();

    if (name) {
      params = params.append('name', `${name}`);
    }
    if (bypassCache) {
      params = params.append('_', `${Date.now()}`);
    }

    return this.http.get<ISettingsApiModel>(
      `${environment.API_URL}v5/webapp/consumer/settings.json`,
      { params },
    );
  }

  private initHeadMetatag(
    configsUrl: string,
    settings: ISettingsApiModel,
  ): void {
    const configName = settings.config_name;
    const customFavicon = settings.favicon?.trim();
    const fallbackFavicon = '/assets/favicon.ico';
    const primaryFavicon = customFavicon
      ? this.addSettingsAssetVersion(
          this.resolveSettingsAssetUrl(configsUrl, customFavicon),
          settings,
        )
      : fallbackFavicon;

    this.removeManagedIconLinks();
    this.upsertIconLink(
      'icon',
      primaryFavicon,
      null,
      'image/x-icon',
      'app-favicon',
    );

    if (!customFavicon) {
      this.metaService.removeTag("name='msapplication-TileImage'");
      return;
    }

    const appleIconsSizes = [
      '57x57',
      '60x60',
      '72x72',
      '76x76',
      '114x114',
      '120x120',
      '144x144',
      '152x152',
      '180x180',
    ];
    for (const appleIconsSize of appleIconsSizes) {
      this.upsertIconLink(
        'apple-touch-icon',
        this.addSettingsAssetVersion(
          `${configsUrl}configs/${configName}/apple-icon-${appleIconsSize}.png`,
          settings,
        ),
        appleIconsSize,
        null,
        null,
        true,
      );
    }

    this.upsertIconLink(
      'icon',
      this.addSettingsAssetVersion(
        `${configsUrl}configs/${configName}/android-icon-192x192.png`,
        settings,
      ),
      '192x192',
      'image/png',
      null,
      true,
    );

    const faviconsSizes = ['32x32', '96x96', '16x16'];
    for (const faviconsSize of faviconsSizes) {
      this.upsertIconLink(
        'icon',
        this.addSettingsAssetVersion(
          `${configsUrl}configs/${configName}/favicon-${faviconsSize}.png`,
          settings,
        ),
        faviconsSize,
        'image/png',
        null,
        true,
      );
    }

    this.metaService.updateTag({
      name: 'msapplication-TileImage',
      content: this.addSettingsAssetVersion(
        `${configsUrl}configs/${configName}/ms-icon-144x144.png`,
        settings,
      ),
    });

    // window.top.postMessage('3DS-authentication-complete', '*');
  }

  private upsertIconLink(
    rel: string,
    href: string,
    sizes: string | null = null,
    type: string | null = null,
    id: string | null = null,
    managed = false,
  ): void {
    const sizesSelector = sizes ? `[sizes="${sizes}"]` : ':not([sizes])';
    let link: HTMLLinkElement | null = id
      ? this.document.querySelector<HTMLLinkElement>(`#${id}`)
      : this.document.head.querySelector<HTMLLinkElement>(
          `link[rel="${rel}"]${sizesSelector}`,
        );

    if (!link) {
      link = this.document.createElement('link');
      link.rel = rel;
      if (id) {
        link.id = id;
      }
      if (sizes) {
        link.setAttribute('sizes', sizes);
      }
      this.document.head.appendChild(link);
    }

    link.rel = rel;
    if (managed) {
      // Domino (Angular SSR DOM) does not implement HTMLElement.dataset.
      // eslint-disable-next-line unicorn/prefer-dom-node-dataset
      link.setAttribute('data-btr-settings-icon', 'true');
    }
    link.href = href;
    if (type) {
      link.type = type;
    }
  }

  private removeManagedIconLinks(): void {
    const managedIcons = this.document.head.querySelectorAll(
      'link[data-btr-settings-icon="true"]',
    );
    for (const icon of Array.from(managedIcons)) {
      icon.parentNode?.removeChild(icon);
    }
  }

  private resolveSettingsAssetUrl(baseUrl: string, path: string): string {
    if (/^https?:\/\//i.test(path)) {
      return path;
    }

    const normalizedBaseUrl = baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`;
    return `${normalizedBaseUrl}${path.replace(/^\/+/, '')}`;
  }

  private addSettingsAssetVersion(
    assetUrl: string,
    settings: ISettingsApiModel,
  ): string {
    const version = settings.settings_version ?? settings.date_modified;
    return version == null
      ? assetUrl
      : `${assetUrl}?v=${encodeURIComponent(`${version}`)}`;
  }

  private initMetatagHeaderColor(config: ISettingsApiModel): void {
    this.metaService.updateTag({
      name: 'msapplication-navbutton-color',
      content: config.header_color,
    });
    this.metaService.updateTag({
      name: 'apple-mobile-web-app-status-bar-style',
      content: config.header_color,
    });
  }
}
